import React, {useState, useEffect, useRef, useCallback, useContext} from 'react';
import {observer} from 'mobx-react-lite';
import {ClipTypeEnum, Engine} from "@rendley/sdk";
import {RendleyService} from "../../../../services/RendleyService";
import {RendleyStore} from "../../../../store/RendleyStore";
import {ContextMenuStore} from "../../../../store/ContextMenuStore";
// @ts-ignore
import './MediaPanelContainer.styles.scss';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
    determineTrackThumbnailUrl, EditorMediaTypeEnums,
    formatToReadableShortTitle,
    formatUnixTime, IsUploadedTrackType, IsVideoTrackType, loadTranscriptForTrack,
    RefreshTokenAndRetry,
    formatDurationBrief, handleDownloadButtonClick
} from "../../../../../utils/utils";
import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    CircularProgress,
    TextField
} from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DownloadIcon from '@mui/icons-material/Download';
import {FetchContext} from "../../../../../context/FetchContext";
import {AuthContext} from "../../../../../context/AuthContext";
import {AudioWaveformIcon, VideoCameraIcon} from "../../../../../pages/NewHome/ProjectThumbnail";
import {useConfirmDialog} from "../../../../../pages/NewHome/DeleteConfirmation";
import {SortButton} from "../../../../../pages/NewHome/HomeButtons";
import {
    CLIP_LIBRETTO_CLIP_ID, GenerateLibrettoClipId, MEDIA_DURATION,
    MEDIA_ENTITY_ID,
    MEDIA_TRANSCRIPT,
    MEDIA_TRANSCRIPT_URL,
    MEDIA_WAVEFORM_DATA
} from "../../../../Constants";
import {doc, updateDoc} from "firebase/firestore";
import {db} from "../../../../../Firebase";
import {kAssetsCollectionName, kTracksCollectionName} from "../../../../../utils/CollectionConstants";

interface MediaPanelContainerProps {
    onClose?: () => void;
    projectTracks: any;
    projectImages: any;
    // @ts-ignore
}

interface Track {
    assetId?: string; // Make sure to include this if optional
    trackId?: string;
    thumbnailUrl?: string;
    title?: string;
    duration?: number;
    progress?: number;
    trackType?: string;
    status?: string;
    transcriptUrl?: string;
    hasTranscript?: boolean;
    isSilentAudio?: boolean;
    renditionUrl?: string;
    objectUrl?: string;
    objectName?: string;
}

interface MediaItem {
    id: string;
    title: string;
    thumbnailUrl?: string;
    duration?: number;
    type: 'image' | 'video' | 'audio';
    status: string;
    progress?: number;
    track: Track;
}

const MediaPanelContainer: React.FC<MediaPanelContainerProps> = observer(({projectTracks, projectImages}) => {
    const fetchContext = useContext(FetchContext);
    const authContext = useContext(AuthContext);

    const [loadingStates, setLoadingStates] = useState({});

    const [sortedItems, setSortedItems] = useState<MediaItem[]>([]);
    const [sortNewest, setSortNewest] = useState(true);
    const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
    const [renamingItemId, setRenamingItemId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState("");

    const {openDialog, ConfirmDialogComponent} = useConfirmDialog({confirmText: 'Delete', cancelText: 'Cancel'});

    const deleteTrack = async ({trackId}) => {
        fetchContext.authAxios.post(`/tracks/delete`, {
            trackIds: [trackId],
        }, {
            headers: {
                Authorization: `Bearer ${authContext.getToken()}`,
            }
        }).then(() => {
        }).catch((err) => {
            if (err.response.status === 401) {
                RefreshTokenAndRetry(err, authContext, fetchContext);
            }
        })
    }

    const deleteAsset = async ({assetId}) => {
        fetchContext.authAxios.delete(`/assets/${assetId}`, {
            headers: {
                Authorization: `Bearer ${authContext.getToken()}`,
            }
        }).then(() => {

        }).catch((err) => {
            if (err.response.status === 401) {
                RefreshTokenAndRetry(err, authContext, fetchContext);
            }
        })
    };

    const updateAssetTitle = async ({assetId, title}) => {
        // Update Firestore
        const entityRef = doc(db, kAssetsCollectionName, assetId);
        try {
            await updateDoc(entityRef, { title: title });
        } catch (error) {
            console.error('Error updating title: ', error);
        }
    };

    const updateTrackTitle = async ({trackId, title}) => {
        const entityRef = doc(db, kTracksCollectionName, trackId);
        try {
            await updateDoc(entityRef, { title: title });
        } catch (error) {
            console.error('Error updating title: ', error);
        }
    }

    const handleRename = async (itemId: string, newName: string, isImage: boolean) => {
        try {
            if (isImage) {
                await updateAssetTitle({assetId: itemId, title: newName});
            } else {
                await updateTrackTitle({trackId: itemId, title: newName});
            }

            // For now, just update local state to reflect the new name
            setSortedItems(prev => prev.map(item => 
                item.id === itemId ? { 
                    ...item, 
                    title: newName,
                    track: { ...item.track, title: newName }
                } : item
            ));
        } catch (err) {
            console.error('Failed to rename:', err);
            if (err.response?.status === 401) {
                RefreshTokenAndRetry(err, authContext, fetchContext);
            }
        }
        
        setRenamingItemId(null);
        setRenameValue("");
    };

    const startRenaming = (item: MediaItem) => {
        setRenamingItemId(item.id);
        setRenameValue(item.title);
    };

    const cancelRenaming = () => {
        setRenamingItemId(null);
        setRenameValue("");
    };

    const handleContextMenuClick = (event: React.MouseEvent, item: MediaItem) => {
        event.preventDefault();
        event.stopPropagation();
        
        const menuItems = [
            {
                title: "Rename",
                icon: () => <ModeEditIcon />,
                onClick: () => {
                    startRenaming(item);
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Delete", 
                icon: () => <DeleteIcon />,
                onClick: () => {
                    openDialog({
                        title: `Delete this ${item.type}?`,
                        message: 'It will be permanently deleted.',
                        onConfirm: async () => {
                            if (item.type === 'image') {
                                await deleteAsset({assetId: item.id});
                            } else {
                                await deleteTrack({trackId: item.id});
                            }
                        },
                    });
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Download",
                icon: () => <DownloadIcon />,
                onClick: async () => {
                    console.log('Download:', item);
                    await handleDownloadButtonClick({
                        assetTitle: "", 
                        trackTitle: item.track.title, 
                        customTitle: item.track.title, 
                        isRecording: false, 
                        trackUrl: item.track.objectUrl, 
                        trackCreatedAt: null
                    });
                    ContextMenuStore.hide();
                },
            },
        ];
        
        ContextMenuStore.show(event.clientX, event.clientY, menuItems);
    };



    useEffect(() => {
        const allItems: MediaItem[] = [];

        // Add images
        if (projectImages) {
            projectImages.forEach((image: Track) => {
                allItems.push({
                    id: image.assetId || '',
                    title: image.title || '',
                    thumbnailUrl: image.thumbnailUrl,
                    type: 'image',
                    status: image.status || 'Ready',
                    progress: image.progress,
                    track: image
                });
            });
        }

        // Add videos and audio
        if (projectTracks) {
            projectTracks.forEach((track: Track) => {
                const isVideo = IsVideoTrackType(track.trackType);
                allItems.push({
                    id: track.trackId || '',
                    title: track.title || '',
                    thumbnailUrl: track.thumbnailUrl,
                    duration: track.duration,
                    type: isVideo ? 'video' : 'audio',
                    status: track.status || 'Ready',
                    progress: track.progress,
                    track: track
                });
            });
        }

        // Sort items
        const sorted = [...allItems].sort((a, b) => {
            // Assuming createTime exists on track
            const aTime = (a.track as any).createTime || 0;
            const bTime = (b.track as any).createTime || 0;
            return sortNewest ? bTime - aTime : aTime - bTime;
        });

        setSortedItems(sorted);
    }, [projectTracks, projectImages, sortNewest]);


    const handleThumbnailClick = useCallback(async ({track, isImage}) => {

        if (track.status !== "Ready") {
            return;
        }

        const entityId = isImage ? track.assetId : track.trackId;
        setLoadingStates((prevState) => ({
            ...prevState,
            [entityId]: true,
        }));

        await handleClickMedia({track, isImage});

        setLoadingStates((prevState) => ({
            ...prevState,
            [entityId]: false,
        }));
    }, []);


    const handleClickMedia = useCallback(async ({track, isImage}) => {
        if (track.status !== "Ready") {
            return;
        }

        const entityId = isImage ? track.assetId : track.trackId;

        let mediaDataId = null;

        // Try and find the media in the library.
        if (Engine.getInstance().getLibrary().media) {
            const media = Engine.getInstance().getLibrary().media;

            Object.entries(media)?.forEach(([key, value]) => {
                value.customData?.forEach((customDataValue, customDataKey) => {
                    if (customDataKey === "entityId" && customDataValue === entityId) {
                        mediaDataId = key;
                        return;
                    }
                });
                if (mediaDataId != null) return;
            });
        }

        // If not already there, add the media to the library.
        if (!mediaDataId) {
            console.log("No media data found.");
            try {
                const objectUrl = isImage ? track.thumbnailUrl : track.renditionUrl ? track.renditionUrl : track.objectUrl;
                const waveformUrl = isImage || !track.hasAudioStream ? "" : track.wavedataUrl;
                const transcriptUrl = isImage || !track.hasTranscript ? "" : track.transcriptUrl;

                const mimeExt = isImage ? "png" : track.renditionUrl ? track.renditionName.split('.').pop() : track.objectName.split('.').pop();
                const mimeType = isImage ? "image/png" : IsVideoTrackType(track.trackType) ? `video/${mimeExt}` : `audio/${mimeExt}`;

                const fetchObject = fetch(objectUrl).then((response) => response.blob());
                const fetchWaveform = waveformUrl === "" ? null : fetch(waveformUrl).then((response) => response.arrayBuffer());
                const fetchTranscript = (!isImage && track.transcriptUrl != null && track.hasTranscript && !track.isSilentAudio) ?
                    fetch(track.transcriptUrl).then((response) => response.json()).catch(() => {
                        return null;
                    }) : null;

                const [fileBlob, waveform, transcript] = await Promise.all([fetchObject, fetchWaveform, fetchTranscript]);

                const file = new File([fileBlob], track.title, {type: mimeType});

                mediaDataId = await Engine.getInstance().getLibrary().addMedia(file);

                Engine.getInstance().getLibrary().getMediaById(mediaDataId).setPermanentUrl(objectUrl);

                const int16Array = new Int16Array(waveform);

                const renderableWaveform = new Float32Array(int16Array.length);
                for (let i = 0; i < int16Array.length; i++) {
                    renderableWaveform[i] = int16Array[i] / 32768;
                }

                RendleyService.getMediaById(mediaDataId)?.setCustomData(MEDIA_WAVEFORM_DATA, renderableWaveform);
                RendleyService.getMediaById(mediaDataId)?.setCustomData(MEDIA_ENTITY_ID, entityId);

                RendleyService.getMediaById(mediaDataId)?.setCustomData(MEDIA_TRANSCRIPT_URL, track.transcriptUrl);
                RendleyService.getMediaById(mediaDataId)?.setCustomData(MEDIA_TRANSCRIPT, transcript);
                RendleyService.getMediaById(mediaDataId)?.setCustomData(MEDIA_DURATION, track.duration);

            } catch (e) {
                console.error(e);
            }
        } else {
            console.log("Media data Id already exists!");
        }

        // Creates layer in Rendley Engine.
        const layer = RendleyService.createLayer();

        // Updates the layer with a clip in the Rendley Engine.
        const clip = await RendleyService.addMediaToLayer(layer.id, mediaDataId);

        // Scale the clip to fit the canvas
        if (clip && track.resolution) {
            // Parse track resolution (format: "widthxheight")
            const resolutionMatch = track.resolution.match(/(\d+)x(\d+)/);
            if (resolutionMatch) {
                const trackWidth = parseInt(resolutionMatch[1]);
                const trackHeight = parseInt(resolutionMatch[2]);
                
                // Get canvas resolution
                const canvasWidth = RendleyStore.display.width;
                const canvasHeight = RendleyStore.display.height;

                if (canvasWidth > 0 && canvasHeight > 0 && trackWidth > 0 && trackHeight > 0) {
                    // Calculate scale to fit within canvas while maintaining aspect ratio
                    const scaleX = canvasWidth / trackWidth;
                    const scaleY = canvasHeight / trackHeight;
                    
                    // Use the smaller scale to ensure the clip fits within canvas while maintaining aspect ratio
                    const scale = Math.min(scaleX, scaleY);
                    
                    // Apply the scale to the clip after a short delay to ensure it's initialized
                    setTimeout(() => {
                        clip.style.setScale(scale, scale);
                    }, 100);
                }
            }
        }

        // Update LibrettoClip related abstraction in Libretto world.
        const librettoClipId = GenerateLibrettoClipId();
        clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
        RendleyStore.addClipIdForLibrettoClip(librettoClipId, clip.id);
        RendleyStore.setLayerIdForLibrettoClip(librettoClipId, layer.id);
        RendleyStore.addLibrettoClipIdToLayer(librettoClipId, layer.id);

    }, []);


    const handleClickMediaThumbnail = useCallback(async ({track, isImage}) => {

        if (track.status !== "Ready") {
            return;
        }

        const entityId = isImage ? track.assetId : track.trackId;

        let mediaDataId = null;

        // Try and find the media in the library.
        if (Engine.getInstance().getLibrary().media) {
            const media = Engine.getInstance().getLibrary().media;

            Object.entries(media)?.forEach(([key, value]) => {
                value.customData?.forEach((customDataValue, customDataKey) => {
                    if (customDataKey === "entityId" && customDataValue === entityId) {
                        mediaDataId = key;
                        return;
                    }
                });
                if (mediaDataId != null) return;
            });
        }

        // If not already there, add the media to the library.
        if (!mediaDataId) {
            try {
                const objectUrl = isImage ? track.thumbnailUrl : track.renditionUrl ? track.renditionUrl : track.objectUrl;
                const audioExtension = !isImage && !IsVideoTrackType(track.trackType) ? track.objectName.split('.').pop() : null;
                const mimeType = isImage ? "image/png" : IsVideoTrackType(track.trackType) ? "video/mp4" : `audio/${audioExtension}`;

                // Parallelize the fetching of the object and the transcript
                const fetchObject = fetch(objectUrl).then((response) => response.blob());
                const fetchTranscript = (!isImage && track.transcriptUrl != null && track.hasTranscript && !track.isSilentAudio) ?
                    fetch(track.transcriptUrl).then((response) => response.json()).catch(() => {
                        return null;
                    }) : null;

                const [blob, transcript] = await Promise.all([fetchObject, fetchTranscript]);

                const file = new File([blob], track.title, {type: mimeType});

                // Add media to gallery
                mediaDataId = await RendleyService.addMediaToGallery(file, mimeType);
                if (mediaDataId == null) return;
                RendleyService.getMediaById(mediaDataId)?.setPermanentUrl(objectUrl);
                RendleyService.getMediaById(mediaDataId)?.setCustomData("entityId", entityId);
                RendleyService.getMediaById(mediaDataId)?.setCustomData("transcriptUrl", track.transcriptUrl);
                RendleyService.getMediaById(mediaDataId)?.setCustomData("transcript", transcript);

                window.dispatchEvent(new CustomEvent("LIBRETTO_LIBRARY_ADDED", {
                    detail: {
                        filename: track.title,
                        id: mediaDataId,
                        permanentUrl: objectUrl,
                        mimeType: mimeType,
                        entityId: entityId,
                        transcriptUrl: track.hasTranscript ? track.transcriptUrl : "",
                    }
                }));
            } catch (error) {
                console.error("Error adding media to gallery: ", error);
            }
        } else {
            console.log("Media already in library.");
        }

        // Add the clip to the timeline.
        try {
            console.log("Ready to add clip to timeline");
            const layer = Engine.getInstance().getTimeline().createLayer();
            const clip = await layer.addClip({mediaDataId: mediaDataId, startTime: 0});
            if (clip == null) {
                console.log("Clip is null")
                return
            } else {
                console.log("Clip added to layer")
            }
            RendleyService.getClipById(clip.id)?.setCustomData("layerId", layer.id);
            // RendleyStore.setFilenameByClipId(clip.id, track.title);
        } catch (error) {
            console.error(error);
        }
    }, []);

    // @ts-ignore
    return (
        <div style={{width: '100%', maxWidth: "300px"}}>
            <ConfirmDialogComponent/>

            {/* Header */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                mx={1}
            >
                <Typography sx={{fontSize: '16px', fontWeight: 500}}>
                    Files
                </Typography>
                <SortButton sortNewest={sortNewest} onClick={() => setSortNewest(!sortNewest)} iconOnly={true}/>
            </Box>

            {/* List of files */}
            {sortedItems && sortedItems.length > 0 ? (
                <List sx={{width: '100%', bgcolor: 'background.paper', py: 0, px: 0}}> {/* No padding on list */}
                    {sortedItems.map((item) => (
                        <ListItem
                            key={item.id}
                            disablePadding
                            onMouseEnter={() => setHoveredItemId(item.id)}
                            onMouseLeave={() => setHoveredItemId(null)}
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                }
                            }}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    aria-label="more"
                                    size="small"
                                    onClick={(e) => handleContextMenuClick(e, item)}
                                    sx={{
                                        mr: 0.1, // Match the left padding
                                        p: 0.5, // Small padding for the button
                                        opacity: hoveredItemId === item.id ? 1 : 0,
                                        pointerEvents: hoveredItemId === item.id ? 'auto' : 'none',
                                        transition: 'opacity 0.2s ease'
                                    }}
                                >
                                    <MoreVertIcon fontSize="small"/>
                                </IconButton>
                            }
                        >
                            <ListItemButton
                                onClick={() => {
                                    if (renamingItemId !== item.id) {
                                        handleThumbnailClick({
                                            track: item.track,
                                            isImage: item.type === 'image'
                                        });
                                    }
                                }}
                                disabled={item.status !== 'Ready' || loadingStates[item.id]}
                                sx={{
                                    py: 0.75, // Reduced padding
                                    pl: 0.5, // Tiny left padding
                                    pr: 3, // Account for secondaryAction
                                    '&.Mui-disabled': {
                                        opacity: 0.6,
                                    }
                                }}
                            >
                                <ListItemIcon sx={{minWidth: 32, mr: 0.75}}> {/* Reduced spacing */}
                                    {loadingStates[item.id] ? (
                                        <CircularProgress size={20}/>
                                    ) : (
                                        <Box
                                            sx={{
                                                width: 32, // Small thumbnail
                                                height: 32, // Small thumbnail
                                                borderRadius: '4px',
                                                backgroundColor: '#E3E5E8',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {item.thumbnailUrl ? (
                                                <img
                                                    src={fetchContext.getProxiedURL(item.thumbnailUrl)}
                                                    alt={item.title}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            ) : item.type === 'image' ? (
                                                <ImageIcon sx={{color: '#666', fontSize: 20}}/>
                                            ) : item.type === 'video' ? (
                                                <VideoCameraIcon backgroundColor="#E3E5E8"/>
                                            ) : (
                                                <AudioWaveformIcon backgroundColor="#E3E5E8"/>
                                            )}
                                        </Box>
                                    )}
                                </ListItemIcon>
                                {renamingItemId === item.id ? (
                                    <TextField
                                        value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onBlur={() => {
                                            if (renameValue.trim() && renameValue !== item.title) {
                                                handleRename(item.id, renameValue.trim(), item.type === 'image');
                                            } else {
                                                cancelRenaming();
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                if (renameValue.trim() && renameValue !== item.title) {
                                                    handleRename(item.id, renameValue.trim(), item.type === 'image');
                                                } else {
                                                    cancelRenaming();
                                                }
                                            } else if (e.key === 'Escape') {
                                                cancelRenaming();
                                            }
                                        }}
                                        autoFocus
                                        size="small"
                                        variant="standard"
                                        sx={{
                                            flex: 1,
                                            '& .MuiInput-input': {
                                                fontSize: '13px',
                                                padding: '0px',
                                            },
                                            '& .MuiInput-underline:before': {
                                                borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                                            },
                                        }}
                                    />
                                ) : (
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={0.5}> {/* Reduced gap */}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '13px',
                                                        fontWeight: 400,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1,
                                                    }}
                                                >
                                                    {formatToReadableShortTitle(item.title)}
                                                </Typography>
                                                {item.duration && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: '11px',
                                                            color: 'text.secondary',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {formatDurationBrief(item.duration)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            item.status !== 'Ready' && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: '11px',
                                                        color: item.status === 'Processing' ? '#F2880C' : 'text.secondary',
                                                    }}
                                                >
                                                    {item.status}
                                                </Typography>
                                            )
                                        }
                                    />
                                )}
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Box px={0}> {/* No padding to match list */}
                    <Typography align="center" color="text.secondary">No files found.</Typography>
                </Box>
            )}


        </div>
    );
});

export default MediaPanelContainer;